import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { preconApi } from "../lib/api";
import { FACILITY_TYPES } from "./ItbCardDrawer";

/** Settings › Sales — stack-ranking: who gets what. Scope-only rule → facility match → catch-all;
 *  workload breaks ties. Each estimator carries a team AM and preferred facility types. */
export default function AssignmentProfilesEditor() {
  const [profiles, setProfiles] = useState(null);
  const [people, setPeople] = useState({ estimators: [], account_managers: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    preconApi.getAssignmentProfiles().then((r) => setProfiles(r.profiles || [])).catch(() => setProfiles([]));
    preconApi.assignable().then((r) => setPeople({ estimators: r.estimators || [], account_managers: r.account_managers || [] })).catch(() => {});
  }, []);

  if (!profiles) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const upd = (i, patch) => setProfiles((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addRow = () => {
    const taken = new Set(profiles.map((p) => p.estimator_email));
    const next = people.estimators.find((e) => !taken.has(e.email));
    setProfiles((ps) => [...ps, { estimator_email: next?.email || "", estimator_name: next?.name || "",
      am_email: "", am_name: "", facility_types: [], scope_only: [], catch_all: false, small_special: false }]);
  };
  const toggleFacility = (i, f) => {
    const cur = profiles[i].facility_types || [];
    upd(i, { facility_types: cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f] });
  };
  const save = async () => {
    setBusy(true);
    try { const r = await preconApi.putAssignmentProfiles(profiles); setProfiles(r.profiles || []); toast.success("Stack ranking saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not saved"); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="settings-assignment-profiles">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Bid Board — assignment stack ranking
          <span className="ml-1 text-xs font-normal text-muted-foreground">(scope-only rule → facility type → catch-all; workload breaks ties)</span></h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addRow} data-testid="add-profile-btn"><Plus className="mr-1 h-3 w-3" /> Add estimator</Button>
          <Button size="sm" className="h-7 text-xs" onClick={save} disabled={busy} data-testid="save-profiles-btn"><Save className="mr-1 h-3 w-3" /> Save</Button>
        </div>
      </div>
      <div className="space-y-3">
        {profiles.map((p, i) => (
          <div key={i} className="rounded border border-border p-3 space-y-2" data-testid={`profile-${i}`}>
            <div className="flex flex-wrap items-center gap-3">
              <select value={p.estimator_email} data-testid={`profile-estimator-${i}`}
                      onChange={(e) => { const est = people.estimators.find((x) => x.email === e.target.value); upd(i, { estimator_email: e.target.value, estimator_name: est?.name || "" }); }}
                      className="h-8 rounded border border-border bg-background px-2 text-sm">
                <option value="">— estimator —</option>
                {people.estimators.map((e) => <option key={e.email} value={e.email}>{e.name || e.email}</option>)}
                {p.estimator_email && !people.estimators.some((e) => e.email === p.estimator_email) && <option value={p.estimator_email}>{p.estimator_name || p.estimator_email}</option>}
              </select>
              <span className="text-xs text-muted-foreground">pairs with AM</span>
              <select value={p.am_email || ""} data-testid={`profile-am-${i}`}
                      onChange={(e) => { const am = people.account_managers.find((x) => x.email === e.target.value); upd(i, { am_email: e.target.value, am_name: am?.name || "" }); }}
                      className="h-8 rounded border border-border bg-background px-2 text-sm">
                <option value="">— none —</option>
                {people.account_managers.map((a) => <option key={a.email} value={a.email}>{a.name || a.email}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs"><Switch checked={!!p.catch_all} onCheckedChange={(v) => upd(i, { catch_all: v })} data-testid={`profile-catchall-${i}`} /> Everything-else (catch-all)</label>
              <label className="flex items-center gap-1.5 text-xs"><Switch checked={!!p.small_special} onCheckedChange={(v) => upd(i, { small_special: v })} data-testid={`profile-small-${i}`} /> Small special projects</label>
              <button className="ml-auto text-muted-foreground hover:text-red-600" data-testid={`profile-remove-${i}`} onClick={() => setProfiles((ps) => ps.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-28 shrink-0 text-muted-foreground">Scope-only codes</span>
              <Input value={(p.scope_only || []).join(", ")} placeholder="e.g. OPW" className="h-7 w-48 text-xs" data-testid={`profile-scopeonly-${i}`}
                     onChange={(e) => upd(i, { scope_only: e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean) })} />
              <span className="text-[10px] text-muted-foreground">jobs made up only of these scopes go to this estimator</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span className="w-28 shrink-0 pt-1 text-xs text-muted-foreground">Facility types</span>
              <div className="flex flex-1 flex-wrap gap-1.5">
                {FACILITY_TYPES.map((f) => (
                  <button key={f} type="button" onClick={() => toggleFacility(i, f)} data-testid={`profile-fac-${i}-${f}`}
                          className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors ${(p.facility_types || []).includes(f) ? "border-[#007BFF] bg-blue-50 text-blue-700" : "border-border text-muted-foreground hover:border-slate-400"}`}>{f}</button>))}
              </div>
            </div>
          </div>))}
        {!profiles.length && <p className="text-xs text-muted-foreground">No profiles yet — add an estimator to start ranking.</p>}
      </div>
    </section>
  );
}
